import { Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** Compact variant for inside a card or a table body. */
  size?: 'sm' | 'md';
  className?: string;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        className,
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center rounded-full bg-surface-sunken text-fg-subtle',
          size === 'sm' ? 'h-9 w-9' : 'h-12 w-12',
        )}
      >
        <Icon className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
      </span>
      <div className="space-y-1">
        <p className={cn('font-medium text-fg', size === 'sm' ? 'text-sm' : 'text-base')}>{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-sm text-muted">{description}</p>
        ) : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/** Error counterpart — same shape, danger tone, and a retry slot. */
export function ErrorState({
  title = 'Could not load this',
  description,
  onRetry,
  className,
  size = 'md',
}: {
  title?: string;
  description?: React.ReactNode;
  onRetry?: () => void;
  className?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        size === 'sm' ? 'px-4 py-8' : 'px-6 py-12',
        className,
      )}
      role="alert"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-danger-soft text-danger">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
          <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium text-fg">{title}</p>
        {description ? <p className="mx-auto max-w-sm text-sm text-muted">{description}</p> : null}
      </div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border border-subtle bg-surface px-3 py-1.5 text-sm font-medium text-fg shadow-xs hover:bg-surface-hover"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}
